<template>
    <b-row>
        <b-col>
            <card title="Patient Insurance Uploads">
                <div v-if="items && items.length">
                    <div v-for="ins in items" :key="`${ins.id}_${ins.fromPortal || ''}`">
                        <card>
                            <div style="cursor:pointer" v-on:click="toggleInfo(ins)"
                                v-b-toggle="'panel-' + ins.id.toString()">
                                <h6>
                                    {{ ins.patientName }}
                                    <b-badge v-if="hasAIParsedData(ins)" variant="info" class="ml-2" v-b-tooltip.hover title="AI parsed data available">
                                        <i class="fas fa-robot"></i> AI Parsed
                                    </b-badge>
                                </h6>
                                <span>Uploaded {{ getAge(ins.uploaded) }}</span> <br>
                                <span v-if="ins.fromPortal == 'BrandPortal'">Requested by {{ ins.programName }}</span>
                            </div>

                            <b-collapse accordion :id="'panel-' + ins.id.toString()"
                                :ref="'panel-' + ins.id.toString()">
                                <hr style="border-width:3px" />

                                <b-row>
                                    <b-col cols="6">
                                        <!-- PATIENT INFORMATION -->
                                        <b-row>
                                            <b-col cols="4">
                                                <b>Date Of Birth: </b>{{ convertDate(ins.patientDOB) }} <br />
                                                <b>Phone: </b>{{ ins.phoneNumber | formatPhone }}<br />
                                                <b>Cell: </b>{{ ins.cellNumber | formatPhone }}
                                            </b-col>
                                            <b-col cols="4">
                                                {{ ins.patientAddress1 }} <br />
                                                {{ ins.patientAddress2 }}<br v-if="ins.patientAddress2?.length > 0" />
                                                {{ ins.patientAddressCity }}, {{ ins.patientAddressState }}
                                            </b-col>
                                            <b-col>
                                                <router-link v-if="ins.patKey" target="_blank"
                                                    :to="{ name: 'PatientDashboard', params: { id: ins.patKey } }">
                                                    <span>Go To Profile</span> <i class="fas fa-user"></i>
                                                </router-link>
                                            </b-col>
                                        </b-row>
                                        <hr />
                                        <!-- PATIENT PORTAL INSURANCE UPLOADS-->
                                        <b-row>
                                            <b-col>
                                                <div style="width:500px;height:600px;display:block;">
                                                    <viewer v-if="patient.id == ins.patKey" class="viewer"
                                                        :id="'viewer-' + ins.id.toString()"
                                                        :ref="'viewer-' + ins.id.toString()" :options="viewerOptions()"
                                                        :images="ins.imageURLs" style="width:500px;height:600px;">
                                                        <template #default="scope">
                                                            <img v-for="src in scope.images" :src="src" :key="src"
                                                                :alt="src" class="d-none">
                                                        </template>
                                                    </viewer>
                                                </div>
                                                <h4 style="text-align:center"
                                                    v-if="!ins.imageURLs || ins.imageURLs.length == 0">NO IMAGES
                                                    UPLOADED</h4>
                                            </b-col>
                                        </b-row>
                                        <!-- BRAN PORTAL INSURANCE UPDATE REQUESTS-->
                                        <b-row v-if="ins.fromPortal == 'BrandPortal'">
                                            <b-col>
                                                <h3>Update requested by {{ ins.programName }}</h3>
                                                <b-row>
                                                    <b-col>
                                                        <b>Plan Name: </b> <span>{{ ins.planName }}</span>
                                                    </b-col>
                                                    <b-col>
                                                        <b>BIN: </b> <span>{{ ins.bin }}</span>
                                                    </b-col>
                                                    <b-col>
                                                        <b>PCN: </b> <span>{{ ins.pcn }}</span>
                                                    </b-col>
                                                    <b-col>
                                                        <b>Provider Type: </b> <span>{{ ins.providerTypeDesc }}</span>
                                                    </b-col>
                                                </b-row>
                                                <hr>
                                                <b-row>
                                                    <b-col>
                                                        <b>Group Id: </b> <span>{{ ins.groupId }}</span>
                                                    </b-col>
                                                    <b-col>
                                                        <b>Cardholder Id: </b> <span>{{ ins.cardholderId }}</span>
                                                    </b-col>
                                                    <b-col>
                                                        <b>Person Code: </b> <span>{{ ins.personCode }}</span>
                                                    </b-col>
                                                    <b-col>
                                                        <b>Relationship: </b> <span>{{ ins.relationshipDesc }}</span>
                                                    </b-col>
                                                    <b-col cols="12">
                                                        <b>Comments: </b> <br>
                                                        <span>{{ ins.comments }}</span>
                                                    </b-col>
                                                </b-row>
                                            </b-col>
                                        </b-row>

                                        <b-button variant="success" @click="processInsurance(ins)">Processed</b-button>
                                    </b-col>
                                    <b-col cols="6">
                                        <PatientPlansComponent v-if="patient.id == ins.patKey" v-model="patient"
                                            :id="patient.id.toString()"
                                            :parsedInsuranceData="getParsedData(ins)"
                                            @insurance-processed="onInsuranceProcessed(ins, $event)">
                                        </PatientPlansComponent>

                                        <!-- AI PARSED DATA DISPLAY -->
                                        <card plain v-if="hasAIParsedData(ins) && getParsedData(ins)" class="mt-3">
                                            <h5><i class="fas fa-robot"></i> AI Parsed Insurance Data</h5>
                                            <b-row>
                                                <b-col>
                                                    <b>Insurance Name: </b> <span>{{ getParsedData(ins).insuranceCompanyName?.shortName || getParsedData(ins).insuranceCompanyName?.longName || 'N/A' }}</span>
                                                </b-col>
                                                <b-col>
                                                    <b>BIN: </b> <span>{{ getParsedData(ins).bin || 'N/A' }}</span>
                                                </b-col>
                                                <b-col>
                                                    <b>PCN: </b> <span>{{ getParsedData(ins).pcn || 'N/A' }}</span>
                                                </b-col>
                                                <b-col>
                                                    <b>PBM: </b> <span>{{ getParsedData(ins).pbm || 'N/A' }}</span>
                                                </b-col>
                                            </b-row>
                                            <hr>
                                            <b-row>
                                                <b-col>
                                                    <b>Cardholder Name (from card): </b> <span>{{ getParsedData(ins).patientName || 'N/A' }}</span>
                                                </b-col>
                                                <b-col>
                                                    <b>Group Id: </b> <span>{{ getParsedData(ins).groupId || 'N/A' }}</span>
                                                </b-col>
                                                <b-col>
                                                    <b>Cardholder Id: </b> <span>{{ getParsedData(ins).cardholderId || 'N/A' }}</span>
                                                </b-col>
                                                <b-col>
                                                    <b>Plan Type: </b>
                                                    <b-badge v-if="getParsedData(ins).isMedicaid" variant="warning">Medicaid</b-badge>
                                                    <b-badge v-else-if="getParsedData(ins).isMedicare" variant="info">Medicare</b-badge>
                                                    <b-badge v-else variant="secondary">Commercial</b-badge>
                                                </b-col>
                                            </b-row>
                                            <hr v-if="getParsedData(ins).claimAddress">
                                            <b-row v-if="getParsedData(ins).claimAddress">
                                                <b-col cols="12">
                                                    <b>Claim Address: </b><br>
                                                    <span>{{ getParsedData(ins).claimAddress?.street || '' }}</span><br>
                                                    <span>{{ getParsedData(ins).claimAddress?.city || '' }}, {{ getParsedData(ins).claimAddress?.state || '' }} {{ getParsedData(ins).claimAddress?.postalCode || '' }}</span>
                                                </b-col>
                                            </b-row>
                                        </card>
                                    </b-col>
                                </b-row>

                            </b-collapse>
                        </card>
                    </div>
                </div>
                <div v-else>
                    No Patient Insurance Updates are pending.
                </div>
            </card>
        </b-col>
    </b-row>

</template>

<script lang="ts">
import { Component, Mixins, Watch } from 'vue-property-decorator';
import moment from 'moment';
import { Plan } from '@/models/Plan';
import { PatientPlan, RelationshipCode } from '@/models/PatientPlan';
import { PatientInsuranceUpload } from '@/models/PatientInsuranceUpload';
import SearchComponent from '@/components/SearchComponent.vue';
import { Patient } from '@/models/Patient';
import PatientPlansComponent from '@/components/Insurance/PatientPlansComponent.vue'
import { PatientInsuranceUpdateBase } from '@/models/PatientInsuranceUpdateBase';
import { InsuranceUpdateRequest } from '@/models/InsuranceUpdateRequest';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import 'viewerjs/dist/viewer.css';
import { component as Viewer } from 'v-viewer';
import AutoModeHotkeyMixin from '@/mixins/AutoModeHotkeyMixin';

@Component({
    name: "PatientInsuranceUploads",
    components: {
        SearchComponent,
        PatientPlansComponent,
        Viewer
    },
})

export default class PatientInsuranceUploadsQueue extends Mixins(AutoModeHotkeyMixin) {
    private items: Array<PatientInsuranceUpdateBase> = new Array<PatientInsuranceUpdateBase>();
    private unprocessedInsurances: Array<PatientInsuranceUpload> = new Array<PatientInsuranceUpload>();
    private updateRequests: Array<InsuranceUpdateRequest> = new Array<InsuranceUpdateRequest>();

    private plans: Plan[] = [];
    private addedPlan: Plan = new Plan();
    private addedPatientPlan: PatientPlan = {} as PatientPlan;
    private relationshipCodes = RelationshipCode;
    private currentPatientId: number | string = "";
    private patient: Patient = new Patient();

    viewerOptions() {
        return {
            inline: true,
            debug: true,
            button: false,
            title: false,
            transition: false,
            toolbar: {
                zoomIn: 1,
                zoomOut: 1,
                reset: 1,
                prev: 1,
                next: 1,
                rotateLeft: 1,
                rotateRight: 1,
                flipHorizontal: 1,
                flipVertical: 1,
                oneToOne: 0,
                play: {
                    show: 0,
                    size: 'large'
                }
            }
        };
    }

    async created() {
        await this.loadData();

        const insuranceId = this.$route.params.insuranceId;
        if (insuranceId) {
            await this.loadAndShowItem(parseInt(insuranceId));
        }
    }

    @Watch('$route')
    async onRouteChange(newRoute: any, oldRoute: any) {
        if (newRoute.name !== oldRoute.name) {
            return;
        }

        const oldInsuranceId = oldRoute.params.insuranceId;
        const newInsuranceId = newRoute.params.insuranceId;

        if (oldInsuranceId !== newInsuranceId && newInsuranceId) {
            console.log('Route insuranceId parameter changed, loading new insurance item');
            await this.loadData();
            await this.loadAndShowItem(parseInt(newInsuranceId));
        }
    }

    async loadSingleItem(id: number) {
        try {
            await this.loadData();

            const item = this.items.find(i => i.id === id);

            if (item) {
                this.toggleInfo(item);
                await this.$nextTick();
                this.$root.$emit('bv::toggle::collapse', `panel-${item.id}`);
                return;
            }

            // Item not found in list - load it via API
            const response = await this.$http.get<PatientInsuranceUpdateBase>(`/PatientInsuranceUpload/${id}`);
            const insurance = response.data;

            // Add to the items list if it's not there
            if (insurance && !this.items.find(i => i.id === insurance.id)) {
                if (insurance.fromPortal === 'BrandPortal') {
                    this.updateRequests.unshift(new InsuranceUpdateRequest(insurance));
                } else {
                    this.unprocessedInsurances.unshift(new PatientInsuranceUpload(insurance));
                }
                this.refreshItems();
            }

            // Auto-expand this specific item
            if (insurance) {
                this.toggleInfo(insurance);
                await this.$nextTick();
                this.$root.$emit('bv::toggle::collapse', `panel-${insurance.id}`);
            }
        } catch (error) {
            console.error('Error loading single insurance upload:', error);
            this.$notification(NotificationOptions.error('Failed to load insurance upload'));
        }
    }

    refreshItems() {
        this.items = new Array<PatientInsuranceUpdateBase>();
        this.items = (this.unprocessedInsurances as Array<PatientInsuranceUpdateBase>).concat(this.updateRequests);
        console.log('items', this.items)
        this.items.sort((a, b) => {
            return new Date(a.updated).getTime() - new Date(b.updated).getTime();
        });
    }

    async loadData() {
        await Promise.all([
            this.getUnprocessedInsuranceUploads(),
            this.getUnprocessedInsuranceUpdateRequests()
        ]);
        // Refresh items once after both data sources are loaded to avoid race condition
        this.refreshItems();
    }

    async loadAndShowItem(id: number) {
        try {
            const item = this.items.find(i => i.id === id);

            if (!item) {
                // Item not found in list - try loading it individually
                await this.loadSingleItem(id);
                return;
            }

            this.toggleInfo(item);
            await this.$nextTick();
            this.$root.$emit('bv::toggle::collapse', `panel-${item.id}`);
        } catch (error) {
            console.error('Error loading and showing insurance item:', error);
            this.$notification(NotificationOptions.error('Failed to load insurance upload'));
        }
    }

    async getUnprocessedInsuranceUploads() {
        try {
            const response = await this.$http.get<Array<PatientInsuranceUpload>>(`/PatientInsuranceUpload/Unprocessed`);
            console.log('PatientInsuranceUpload', response.data);
            this.unprocessedInsurances = response.data.map(i => new PatientInsuranceUpload(i));
        } catch (error) {
            console.error("Error while loading patient insurance uploads.", { error, response: error?.response });
            this.$notification(NotificationOptions.error(error));
        }
    }

    async getUnprocessedInsuranceUpdateRequests() {
        try {
            const response = await this.$http.get<Array<InsuranceUpdateRequest>>(`/PatientInsuranceUpload/unprocessed-insurance-update-requests`);
            console.log('unprocessed-insurance-update-requests', response.data);
            this.updateRequests = response.data.map(i => new InsuranceUpdateRequest(i));
        } catch (error) {
            console.error("Error while loading insurance updates requests.", { error, response: error?.response });
            this.$notification(NotificationOptions.error(error));
        }
    }

    convertDate(date: string | Date) {
        return moment(date).local().format("L");
    }

    getAge(date: Date) {
        return moment.utc(date).fromNow()
    }

    toggleInfo(insurance: PatientInsuranceUpdateBase) {
        // Close all open panels (both PatientPortal and BrandPortal items)
        this.items.forEach(item => {
            const panelRef = this.$refs['panel-' + item.id] as any;
            if (panelRef && panelRef[0]) {
                panelRef[0].show = false;
            }
        });

        let newPat = new Patient();
        newPat.id = insurance.patKey;
        this.patient = newPat;

        // Rebuild viewer if it exists (it may not exist yet for newly loaded items)
        const viewerRef = this.$refs[`viewer-${insurance.id}`] as any;
        if (viewerRef && viewerRef[0] && viewerRef[0].rebuildViewer) {
            viewerRef[0].rebuildViewer();
        }
    }

    @Watch("viewer")
    patientChanged(patNew: Patient, patOld: Patient) {
        if (patNew.id != patOld.id) {
            this.clear();
        }
    }

    async processInsurance(item: PatientInsuranceUpdateBase) {
        const id = item.id;
        try {
            if (item.fromPortal == "BrandPortal") {
                await this.$http.post(`/PatientInsuranceUpload/process-update-request/${id}`);
                this.$notification(NotificationOptions.notificationOptionsPreset("Processed Successfully", NotificationOptions.NotificationTypes.success))
                await this.loadData(); // Refresh both queues
            } else {
                // Send tracking data if available
                const trackingData = (item as any).trackingData || {};
                await this.$http.post(`/PatientInsuranceUpload/Process/${id}`, trackingData);
                this.$notification(NotificationOptions.notificationOptionsPreset("Processed Successfully", NotificationOptions.NotificationTypes.success))
                await this.loadData(); // Refresh both queues
            }

            await this.handleAutoModeCompletion();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
            console.error("Error while processing uploaded patient insurance", {
                error,
                response: error?.response
            });
        }
    }

    hasAIParsedData(item: PatientInsuranceUpdateBase): boolean {
        const upload = item as any;
        return !!(upload.aiParseData && upload.aiParsedDate);
    }

    getParsedData(item: PatientInsuranceUpdateBase) {
        if (!this.hasAIParsedData(item)) {
            return null;
        }
        const upload = item as any;
        try {
            return JSON.parse(upload.aiParseData);
        } catch (e) {
            console.error('Failed to parse AI insurance data:', e);
            return null;
        }
    }

    onInsuranceProcessed(item: PatientInsuranceUpdateBase, trackingData: { aiParsedDataUsed?: boolean, aiParsedDataEdited?: boolean }) {
        // Store tracking data on the item so processInsurance can use it
        (item as any).trackingData = trackingData;
    }

    async handleAutoModeCompletion() {
        await this.handleAutoModeNextItem();
    }

    clear() {
        this.unprocessedInsurances = new Array<PatientInsuranceUpload>();
    }

}
</script>
